import { thiCvor } from '../../data/hospitals/thiCvor';
import { thiCvIcu } from '../../data/hospitals/thiCvIcu';
import { benTaubOb } from '../../data/hospitals/benTaubOb';
import { benTaubOr } from '../../data/hospitals/benTaubOr';
import { benTaubCardiac } from '../../data/hospitals/benTaubCardiac';
import { benTaubNeuro } from '../../data/hospitals/benTaubNeuro';
import { benTaubPact } from '../../data/hospitals/benTaubPact';
import { benTaubPacu } from '../../data/hospitals/benTaubPacu';
import { benTaubRegional } from '../../data/hospitals/benTaubRegional';
import { benTaubRemote } from '../../data/hospitals/benTaubRemote';
import { benTaubAirway } from '../../data/hospitals/benTaubAirway';
import { mdaNeuro } from '../../data/hospitals/mdaNeuro';
import { mdaAcutePain } from '../../data/hospitals/mdaAcutePain';
import { mdaIcu } from '../../data/hospitals/mdaIcu';
import { vaOr } from '../../data/hospitals/vaOr';
import { vaCardiac } from '../../data/hospitals/vaCardiac';
import { vaRegional } from '../../data/hospitals/vaRegional';
import { vaPain } from '../../data/hospitals/vaPain';
import { tchOr } from '../../data/hospitals/tchOr';
import { tchRegional } from '../../data/hospitals/tchRegional';
import { tchCardiac } from '../../data/hospitals/tchCardiac';
import { bslmcNeuro } from '../../data/hospitals/bslmcNeuro';
import { bslmcMainOr } from '../../data/hospitals/bslmcMainOr';
import thiTools from './texas-heart/tools';
import tchTools from './texas-childrens/tools';

/*
 * Every hospital's rotations, in one place. A rotation is
 *   { id, label, data, tools? }   data = the rotation's content file; tabs render on the shared components
 * A rotation may `extends` another in the same hospital: anything it does not define
 * (data sections, tab components, tools) comes from the parent. That is how a subspecialty
 * keeps the parent's tools while adding its own Overview and guides.
 * Rotations marked `menu: true` sit behind one picker segment instead of their own segment,
 * so a hospital with many rotations keeps a short switcher. `menu.label` names that segment.
 */
export const HOSPITALS = {
  'ben-taub': {
    menu: { label: 'Specialties' },
    rotations: [
      { id: 'or-trauma', label: 'General OR', data: benTaubOr },
      { id: 'ob', label: 'OB', data: benTaubOb },
      { id: 'cardiac', label: 'Cardiac', extends: 'or-trauma', data: benTaubCardiac, menu: true },
      { id: 'neuro', label: 'Neuro', extends: 'or-trauma', data: benTaubNeuro, menu: true },
      { id: 'regional', label: 'Regional', extends: 'or-trauma', data: benTaubRegional, menu: true },
      { id: 'remote', label: 'Remote', extends: 'or-trauma', data: benTaubRemote, menu: true },
      { id: 'pact', label: 'PACT', extends: 'or-trauma', data: benTaubPact, menu: true },
      { id: 'pacu', label: 'PACU', extends: 'or-trauma', data: benTaubPacu, menu: true },
      { id: 'airway', label: 'Airway', extends: 'or-trauma', data: benTaubAirway, menu: true },
    ],
  },
  'st-lukes': {
    rotations: [
      { id: 'main-or', label: 'Main OR', data: bslmcMainOr },
      { id: 'neuro', label: 'Neuro', data: bslmcNeuro },
    ],
  },
  'texas-heart': {
    rotations: [
      { id: 'cvor', label: 'CVOR', data: thiCvor, tools: thiTools },
      { id: 'cv-icu', label: 'CV ICU', extends: 'cvor', data: thiCvIcu },
    ],
  },
  'texas-childrens': {
    rotations: [
      { id: 'or', label: 'OR', data: tchOr, tools: tchTools },
      { id: 'regional', label: 'Regional', data: tchRegional },
      { id: 'cardiac', label: 'Cardiac', extends: 'or', data: tchCardiac },
    ],
  },
  va: {
    rotations: [
      { id: 'or', label: 'OR', data: vaOr },
      { id: 'cardiac', label: 'Cardiac', extends: 'or', data: vaCardiac },
      { id: 'regional', label: 'Regional', extends: 'or', data: vaRegional },
      { id: 'pain', label: 'Pain', extends: 'or', data: vaPain },
    ],
  },
  'md-anderson': {
    rotations: [
      { id: 'neuro', label: 'Neuro', data: mdaNeuro },
      { id: 'acute-pain', label: 'Acute Pain', data: mdaAcutePain },
      { id: 'icu', label: 'ICU', data: mdaIcu },
    ],
  },
};

export const rotationsFor = (hospitalId) => HOSPITALS[hospitalId]?.rotations || [];

/** Label for the picker segment that holds a hospital's `menu` rotations */
export const menuLabelFor = (hospitalId) => HOSPITALS[hospitalId]?.menu?.label || 'More';

/** The rotation with anything it inherits from `extends` folded in */
export const resolveRotation = (hospitalId, rotationId) => {
  const list = rotationsFor(hospitalId);
  const own = list.find((r) => r.id === rotationId);
  if (!own) return null;
  const parent = own.extends ? resolveRotation(hospitalId, own.extends) : null;
  if (!parent) return own;
  return {
    ...parent,
    ...own,
    data: parent.data || own.data ? { ...(parent.data || {}), ...(own.data || {}) } : undefined,
    tools: { ...(parent.tools || {}), ...(own.tools || {}) },
  };
};
